import React, { useState, useEffect, useRef } from "react";
import { Box, TablePagination } from "@mui/material";
import { getDirectoryContents } from "../../../../core/repo/fileRepo";
import { deleteFile } from "../../../../core/repo/fileRepo";
import { updateFile } from "../../../../core/repo/fileRepo";
import { enqueueSnackbar } from "notistack";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { DATA_EXPLORER_URL, VIEW_FILE_URL } from "../../../../utils/routes";
import FilesList from "./FilesList/FilesList";
import {
  EDIT_FILE_FAILURE,
  EDIT_FILE_SUCCESS,
  DELETE_FILE_FAILURE,
  DELETE_FILE_SUCCESS,
  SELECT_SORT_OPTION_ON_FILE_EXPLORER,
  SEARCH_FILES_ON_FILE_EXPLORER,
  SELECT_FILE_CATEGORY_ON_FILE_EXPLORER,
  SELECT_FILE_TYPE_ON_FILE_EXPLORER,
  SELECT_FILE_STATUS_ON_FILE_EXPLORER,
  RESET_FILTERS_ON_FILE_EXPLORER,
  SELECT_CATEGORY_ON_FILE_EXPLORER,
  PAGE_CHANGE_ON_FILE_EXPLORER,
} from "../../../../utils/posthogEvents";
import posthog from "posthog-js";
import {
  setCurrentDirectorySuccess,
  setFilteredCurrentDirectorySuccess,
} from "../../../../core/events/filesEvents";
import EmptyFilesScreen from "./EmptyFilesScreen";
import Loader from "../../../../components/Loader";
import { debounce } from "lodash";

export const sortOptions = [
  { value: "name", label: "Name" },
  { value: "created_at", label: "Last Uploaded" },
  { value: "updated_at", label: "Last Modified" },
];

export function getSortedFiles(items, sortOption) {
  if (!items) return [];

  const sortedItems = [...items].sort((item1, item2) => {
    switch (sortOption) {
      case "name": {
        return item1.name.localeCompare(item2.name);
      }
      case "created_at": {
        return Date.parse(item2.created_at) - Date.parse(item1.created_at);
      }
      case "updated_at": {
        return Date.parse(item2.updated_at) - Date.parse(item1.updated_at);
      }
      default:
        return 0;
    }
  });

  return sortedItems;
}

function FileExplorer() {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { path } = useParams();
  const { currentDirectory, filteredCurrentDirectory } = useSelector(
    (state) => state.files
  );
  const { selectedDomain } = useSelector((state) => state.domain);
  const [sortOption, setSortOption] = useState(sortOptions[1].value);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("All");
  const [status, setStatus] = useState("all");
  const [fileType, setFileType] = useState("all");
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(0);
  const isInitialMount = useRef(true);

  useEffect(() => {
    const directoryPath = path ? decodeURIComponent(path) : "";
    fetchDirectoryContents(directoryPath, page);
  }, [path, page, selectedDomain]);

  useEffect(() => {
    // Get initial values from URL query params
    const searchParams = new URLSearchParams(location.search);
    const sortParam = searchParams.get("sort");
    const categoryParam = searchParams.get("category");
    const statusParam = searchParams.get("status");
    const fileTypeParam = searchParams.get("fileType");
    const pageParam = searchParams.get("page");

    if (sortParam) {
      setSortOption(sortParam);
    }
    if (categoryParam) {
      setCategory(categoryParam);
    }
    if (statusParam) {
      setStatus(statusParam);
    }
    if (fileTypeParam) {
      setFileType(fileTypeParam);
    }
    if (pageParam) {
      setPage(parseInt(pageParam));
    }

    // Apply filters with URL parameters
    if (currentDirectory) {
      let filteredFiles = currentDirectory.files;
      let filteredDirectories = currentDirectory.directories;

      // Apply status filter
      if (statusParam && statusParam !== "all") {
        filteredFiles = filteredFiles.filter(
          (file) => file.status === statusParam
        );
      }

      // Apply file type filter
      if (fileTypeParam && fileTypeParam !== "all") {
        filteredFiles = filteredFiles.filter((file) =>
          fileTypeParam.includes(file.file_type)
        );
      }

      // Apply tag filter
      if (categoryParam && categoryParam !== "All") {
        filteredFiles = filteredFiles.filter((file) =>
          file.category === categoryParam
        );
      }

      // Apply sorting
      if (sortParam) {
        filteredFiles = getSortedFiles(filteredFiles, sortParam);
        filteredDirectories = getSortedFiles(filteredDirectories, sortParam);
      }

      setFilteredCurrentDirectory({
        ...currentDirectory,
        files: filteredFiles,
        directories: filteredDirectories,
      });
    }
  }, [currentDirectory]);

  useEffect(() => {
    return () => {
      setFilteredCurrentDirectory(null);
    };
  }, []);

  useEffect(() => {
    if (selectedDomain && !isInitialMount.current) {
      history.push({
        pathname: DATA_EXPLORER_URL,
      });
      setPage(0);
    }
    isInitialMount.current = false;
  }, [selectedDomain]);

  const setCurrentDirectory = (directory) => {
    dispatch(setCurrentDirectorySuccess(directory));
  };

  const setFilteredCurrentDirectory = (directory) => {
    dispatch(setFilteredCurrentDirectorySuccess(directory));
  };

  const fetchDirectoryContents = async (
    pathFromState = "",
    page = 0,
    query = ""
  ) => {
    setIsLoading(true);
    const directoryPath = path ? decodeURIComponent(path) : "";
    try {
      await dispatch(
        getDirectoryContents(directoryPath, page + 1, selectedDomain?.id, query)
      );
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectDirectory = async (directoryPath) => {
    posthog.capture(SELECT_CATEGORY_ON_FILE_EXPLORER, {
      directoryPath: directoryPath,
    });
    const searchParams = new URLSearchParams(location.search);
    if (page !== 0) {
      searchParams.set("page", 0);
    }
    if (directoryPath) {
      const encodedPath = encodeURIComponent(directoryPath);
      history.push({
        pathname: `${DATA_EXPLORER_URL}/${encodedPath}`,
        search: searchParams.toString(),
      });
    } else {
      history.push({
        pathname: `${DATA_EXPLORER_URL}`,
        search: searchParams.toString(),
      });
    }
  };

  const handleDeleteFile = async (fileId) => {
    try {
      await dispatch(deleteFile(fileId));
      setCurrentDirectory({
        ...currentDirectory,
        files: currentDirectory.files.filter((file) => file.id !== fileId),
      });
      setFilteredCurrentDirectory({
        ...filteredCurrentDirectory,
        files: filteredCurrentDirectory.files.filter(
          (file) => file.id !== fileId
        ),
      });
      enqueueSnackbar("File has been deleted", {
        variant: "success",
      });
      fetchDirectoryContents(currentDirectory?.current_path, page);
      posthog.capture(DELETE_FILE_SUCCESS);
    } catch (e) {
      posthog.capture(DELETE_FILE_FAILURE, { error: e.message });
      console.log(e);
    }
  };

  const handleUpdateFile = async (file) => {
    try {
      await dispatch(updateFile(file));
      setCurrentDirectory({
        ...currentDirectory,
        files: currentDirectory.files.map((f) =>
          f.id === file.id ? { ...f, ...file } : f
        ),
      });
      setFilteredCurrentDirectory({
        ...filteredCurrentDirectory,
        files: filteredCurrentDirectory.files.map((f) =>
          f.id === file.id ? { ...f, ...file } : f
        ),
      });
      enqueueSnackbar("File name has been updated", {
        variant: "success",
      });
      posthog.capture(EDIT_FILE_SUCCESS);
    } catch (e) {
      console.log(e);
      posthog.capture(EDIT_FILE_FAILURE, { error: e.message });
    }
  };

  const handleSelectFile = (file) => {
    const { id, file_type, file_url } = file;
    if (file_type === "docx") {
      window.open(file_url, "_blank", "noopener,noreferrer");
    } else {
      history.push({
        pathname: `${VIEW_FILE_URL}/${id}`,
      });
    }
  };

  const debouncedFetchFilesData = useRef(
    debounce((query) => {
      fetchDirectoryContents(currentDirectory?.path, 0, query);
      setPage(0);
      posthog.capture(SEARCH_FILES_ON_FILE_EXPLORER, {
        query: query,
      });
    }, 400)
  ).current;

  function handleSearchFiles(query) {
    setQuery(query);
    if (!currentDirectory) return;
    debouncedFetchFilesData(query);

    // let filteredFiles = [];
    // let filteredDirectories = [];

    // if (!query) {
    //   filteredFiles = currentDirectory.files;
    //   filteredDirectories = currentDirectory.directories;
    // } else {
    //   query = query.toLowerCase();
    //   filteredFiles = currentDirectory.files.filter((file) =>
    //     file.name.toLowerCase().includes(query)
    //   );
    //   filteredDirectories = currentDirectory.directories.filter((dir) =>
    //     dir.name.toLowerCase().includes(query)
    //   );
    // }
    // const sortedFiles = getSortedFiles(filteredFiles, sortOption);
    // setFilteredCurrentDirectory({
    //   ...currentDirectory,
    //   files: sortedFiles,
    //   directories: filteredDirectories,
    // });
  }

  const updateUrlParams = (updates) => {
    const searchParams = new URLSearchParams(location.search);

    Object.entries(updates).forEach(([key, value]) => {
      if (value && value !== "all" && value !== "All") {
        searchParams.set(key, value);
      } else {
        searchParams.delete(key);
      }
    });

    history.push({
      pathname: location.pathname,
      search: searchParams.toString(),
    });
  };

  function handleSortFiles(sortOption) {
    setSortOption(sortOption);
    updateUrlParams({ sort: sortOption });
    if (!filteredCurrentDirectory) return;

    const sortedFiles = getSortedFiles(
      filteredCurrentDirectory.files,
      sortOption
    );
    const sortedDirectories = getSortedFiles(
      filteredCurrentDirectory.directories,
      sortOption
    );

    setFilteredCurrentDirectory({
      ...filteredCurrentDirectory,
      files: sortedFiles,
      directories: sortedDirectories,
    });
    posthog.capture(SELECT_SORT_OPTION_ON_FILE_EXPLORER, {
      sortOption: sortOption,
    });
  }

  function handleStatusChange(status) {
    setStatus(status);
    updateUrlParams({ status });
    if (!filteredCurrentDirectory) return;

    if (status === "all") {
      setFilteredCurrentDirectory(currentDirectory);
      return;
    }

    const filteredFiles = currentDirectory.files.filter(
      (file) => file.status === status
    );

    setFilteredCurrentDirectory({
      ...filteredCurrentDirectory,
      files: filteredFiles,
    });
    posthog.capture(SELECT_FILE_STATUS_ON_FILE_EXPLORER, {
      status: status,
    });
  }

  function handleFileTypeChange(fileType) {
    setFileType(fileType);
    updateUrlParams({ fileType });
    if (fileType === "all") {
      setFilteredCurrentDirectory(currentDirectory);
      return;
    }

    const filteredFiles = currentDirectory.files.filter((file) =>
      fileType.includes(file.file_type)
    );

    setFilteredCurrentDirectory({
      ...filteredCurrentDirectory,
      files: filteredFiles,
    });
    posthog.capture(SELECT_FILE_TYPE_ON_FILE_EXPLORER, {
      fileType: fileType,
    });
  }

  function handleCategoryChange(category) {
    setCategory(category);
    updateUrlParams({ category });
    if (category === "All") {
      setFilteredCurrentDirectory(currentDirectory);
    } else {
      setFilteredCurrentDirectory({
        ...currentDirectory,
        files: currentDirectory.files.filter((file) => file.category === category),
      });
    }
    posthog.capture(SELECT_FILE_CATEGORY_ON_FILE_EXPLORER, {
      category: category,
    });
  }

  function handleChangePage(event, newPage) {
    setPage(newPage);
    updateUrlParams({ page: newPage });
    posthog.capture(PAGE_CHANGE_ON_FILE_EXPLORER, {
      page: newPage,
    });
    // fetchDirectoryContents(currentDirectory?.path, newPage);
  }

  function handleResetFilters() {
    setQuery("");
    setSortOption(sortOptions[1].value);
    setStatus("all");
    setFileType("all");
    setCategory("All");
    setFilteredCurrentDirectory(currentDirectory);
    posthog.capture(RESET_FILTERS_ON_FILE_EXPLORER);
  }

  return (
    <Box flex={1} overflow={"auto"}>
      {currentDirectory && filteredCurrentDirectory && (
        <>
          <Box px={3} pt={3} pb={2}>
            <FilesList
              category={category}
              query={query}
              sortOption={sortOption}
              status={status}
              fileType={fileType}
              filteredCurrentDirectory={filteredCurrentDirectory}
              currentDirectory={currentDirectory}
              handleSelectFile={handleSelectFile}
              handleDeleteFile={handleDeleteFile}
              handleUpdateFile={handleUpdateFile}
              handleSelectDirectory={handleSelectDirectory}
              handleSearchFiles={handleSearchFiles}
              handleSortFiles={handleSortFiles}
              handleStatusChange={handleStatusChange}
              handleFileTypeChange={handleFileTypeChange}
              handleResetFilters={handleResetFilters}
              handleCategoryChange={handleCategoryChange}
            />
          </Box>
          {currentDirectory?.pagination?.total_files > 0 && (
            <Box pr={3}>
              <TablePagination
                component={Box}
                count={currentDirectory?.pagination?.total_files}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={10}
                rowsPerPageOptions={[10]}
              />
            </Box>
          )}
        </>
      )}
      {!currentDirectory && !isLoading && <EmptyFilesScreen />}
      {isLoading && <Loader />}
    </Box>
  );
}

export default FileExplorer;
